import { Component } from '@theme/component';

/**
 * Fake filter step for lingerie sizes (cf. .docs/product-page-integration.md décision 4).
 *
 * Wraps a bonnet radio group (`[data-bonnet-filter]`, no `data-option-index`) and the real size
 * option fieldset whose labels carry `data-bonnet`. Picking a bonnet only toggles which size
 * values are visible — it never selects a variant, and its `change` events are stopped here so
 * the surrounding `variant-picker` logic never sees them. Switching bonnet keeps an already
 * selected size checked (it is a filter, not a reset); the selection stays visible in the CTA.
 */
class BonnetFilter extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.addEventListener('change', this.#onChange, { signal: this.#abortController.signal });
    this.#initSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Re-rendered by the Section Rendering API (color swap): the sibling may expose a different set
   * of bonnets and its labels come back unfiltered, so redo the preselection + filtering pass.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#initSelection();
  }

  /** @returns {HTMLInputElement[]} */
  get #bonnetRadios() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[data-bonnet-filter]')));
  }

  /** Preselect the bonnet of the already-selected size, else the first bonnet, then filter. */
  #initSelection() {
    if (this.#bonnetRadios.some((radio) => radio.checked)) {
      this.#applyFilter();
      return;
    }

    /** @type {HTMLElement | null} */
    const selectedSize = this.querySelector('label[data-bonnet]:has(input:checked)');
    const targetBonnet = selectedSize?.dataset.bonnet;

    const radioToCheck = this.#bonnetRadios.find((radio) => radio.value === targetBonnet) ?? this.#bonnetRadios[0];
    if (radioToCheck) radioToCheck.checked = true;

    this.#applyFilter();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.hasAttribute('data-bonnet-filter')) return;

    // The bonnet is not a variant option: keep its change events away from variant-picker.
    event.stopPropagation();
    this.#applyFilter();
  };

  #applyFilter() {
    const bonnet = this.#bonnetRadios.find((radio) => radio.checked)?.value;

    for (const label of this.querySelectorAll('label[data-bonnet]')) {
      if (!(label instanceof HTMLElement)) continue;
      label.hidden = Boolean(bonnet) && label.dataset.bonnet !== bonnet;
    }
  }
}

if (!customElements.get('bonnet-filter')) {
  customElements.define('bonnet-filter', BonnetFilter);
}
